/**
 * ModuleStore – lightweight dependency-change propagator.
 *
 * Keeps a central registry of the latest ModuleExportData for every module.
 * When a source-work-tool fires `swt:change`, the store updates its entry
 * and pushes the new dependencyData array to every element whose
 * configuration.dependencies lists the changed module.
 */
export default class ModuleStore {
  constructor() {
    /** @type {Record<string, object>} moduleId → latest ModuleExportData */
    this._store = {};
    /** @type {Element[]} all registered source-work-tool elements */
    this._elements = [];
    this._onSwtChange = this._onSwtChange.bind(this);
  }

  /**
   * Register elements and seed the store from their existing dependencyData.
   * Call this after initSourceWorkTool() has already set element.dependencyData.
   *
   * @param {NodeList|Element[]} elements
   */
  register(elements) {
    this._elements = Array.from(elements);

    // Seed store from the initial dependencyData already on each element.
    // Later entries for the same moduleId overwrite earlier ones – that is fine
    // because they should all be identical at boot time.
    this._elements.forEach((el) => {
      if (!Array.isArray(el.dependencyData)) return;
      el.dependencyData.forEach((entry) => {
        if (entry && entry.moduleId) {
          this._store[entry.moduleId] = entry;
        }
      });
    });

    document.addEventListener('swt:change', this._onSwtChange);
  }

  /**
   * Remove the global listener (call on teardown if needed).
   */
  destroy() {
    document.removeEventListener('swt:change', this._onSwtChange);
  }

  /** @private */
  _onSwtChange(e) {
    const exported = e.detail?.exportModule;
    if (!exported || !exported.moduleId) return;

    const changedId = exported.moduleId;

    // Update the central store entry.
    this._store[changedId] = exported;

    // Push the refreshed dependencyData to every element that depends on this module.
    this._elements.forEach((el) => {
      const deps = el.configuration?.dependencies;
      if (!Array.isArray(deps) || !deps.includes(changedId)) return;

      const current = Array.isArray(el.dependencyData) ? el.dependencyData : [];

      // Replace the existing entry for changedId, or append if not present yet.
      const next = current.filter((d) => d.moduleId !== changedId);
      next.push(exported);

      el.dependencyData = next;
    });
  }
}
